import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUserShield, FaChevronDown, FaSun, FaMoon, FaAdjust, FaSignOutAlt } from 'react-icons/fa';
import { useLang } from '../context/LangContext';
import { useVisitor } from '../context/VisitorContext';

const languages = [
  { code: 'zh-CN', label: '中文', flag: '🇨🇳' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
];

const themeList = [
  { id: 'light', key: 'light', icon: FaSun },
  { id: 'dark', key: 'dark', icon: FaMoon },
  { id: 'auto', key: 'auto', icon: FaAdjust },
];

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem('gglancc_theme', theme);
}

function Header() {
  const { lang, switchLang, t } = useLang();
  const { visitor, logout, promptLogin } = useVisitor();
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() => {
    return localStorage.getItem('gglancc_theme') || 'dark';
  });

  // Apply theme on mount
  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    if (!langOpen && !themeOpen && !userMenuOpen) return;
    const close = (e) => {
      if (langOpen && !e.target.closest('.lang-dropdown-wrap')) setLangOpen(false);
      if (themeOpen && !e.target.closest('.theme-toggle-wrap')) setThemeOpen(false);
      if (userMenuOpen && !e.target.closest('.visitor-menu-wrap')) setUserMenuOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [langOpen, themeOpen, userMenuOpen]);

  const switchLanguage = (code) => {
    setLangOpen(false);
    if (code === lang) return;
    switchLang(code);
  };

  const switchTheme = (id) => {
    setThemeOpen(false);
    setCurrentTheme(id);
    applyTheme(id);
  };

  const activeLang = languages.find(l => l.code === lang) || languages[0];
  const activeTheme = themeList.find(th => th.id === currentTheme) || themeList[1];
  const ThemeIcon = activeTheme.icon;

  return (
    <header className={`top-header ${scrolled ? 'top-header-scrolled' : ''}`}>
      <div className="top-header-content">
        {/* Left: Brand */}
        <div className="top-header-left">
          <Link to="/" className="top-header-brand">
            <img src="/logo.svg" alt="GGLANCC" className="top-header-logo-img" />
            <span className="top-header-name">GGLANCC</span>
          </Link>
        </div>

        {/* Right: Theme + Language + Admin */}
        <div className="top-header-right">
          {/* Theme Toggle */}
          <div className="theme-toggle-wrap">
            <button
              className="theme-toggle-btn"
              onClick={(e) => { e.stopPropagation(); setThemeOpen(!themeOpen); setLangOpen(false); }}
              title={`Theme: ${t(activeTheme.key)}`}
            >
              <ThemeIcon />
            </button>

            {themeOpen && (
              <div className="theme-dropdown">
                {themeList.map(th => {
                  const Icon = th.icon;
                  return (
                    <button
                      key={th.id}
                      className={`theme-option ${currentTheme === th.id ? 'theme-option-active' : ''}`}
                      onClick={() => switchTheme(th.id)}
                    >
                      <Icon />
                      <span>{t(th.key)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Language Dropdown */}
          <div className="lang-dropdown-wrap">
            <button
              className="lang-toggle-btn"
              onClick={(e) => { e.stopPropagation(); setLangOpen(!langOpen); setThemeOpen(false); }}
            >
              <span className="lang-flag">{activeLang.flag}</span>
              <span className="lang-label">{activeLang.label}</span>
              <FaChevronDown className={`lang-arrow ${langOpen ? 'lang-arrow-open' : ''}`} />
            </button>

            {langOpen && (
              <div className="lang-dropdown">
                {languages.map(l => (
                  <button
                    key={l.code}
                    className={`lang-option ${lang === l.code ? 'lang-option-active' : ''}`}
                    onClick={() => switchLanguage(l.code)}
                  >
                    <span className="lang-flag">{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Visitor avatar or sign in */}
          {visitor ? (
            <div className="visitor-menu-wrap">
              <button
                className="visitor-avatar-btn"
                onClick={(e) => { e.stopPropagation(); setUserMenuOpen(!userMenuOpen); }}
              >
                <img src={visitor.avatar} alt={visitor.name} className="visitor-avatar" />
              </button>
              {userMenuOpen && (
                <div className="visitor-dropdown">
                  <div className="visitor-dropdown-info">
                    <img src={visitor.avatar} alt="" className="visitor-dropdown-avatar" />
                    <div>
                      <div className="visitor-dropdown-name">{visitor.name}</div>
                      <div className="visitor-dropdown-email">{visitor.email}</div>
                    </div>
                  </div>
                  <button className="visitor-dropdown-btn" onClick={() => { logout(); setUserMenuOpen(false); }}>
                    <FaSignOutAlt /> {t('logout')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="visitor-signin-btn" onClick={promptLogin}>
              <img src="https://www.google.com/favicon.ico" alt="" className="google-mini-icon" />
            </button>
          )}

          {/* Admin Link */}
          <Link to="/admin/login" className="top-header-admin" title="Admin Panel">
            <FaUserShield />
          </Link>
        </div>
      </div>
    </header>
  );
}

export default Header;
